import { type ServiceParams, unwrapJson } from "../client";
import type {
  CreateTenantDefaultPolicyPayload,
  LegacyPolicyOut,
  UpdateDataProtectionPayload,
} from "./types";

/**
 * Data Protection service — wraps ``/v1/admin/policies`` (legacy
 * ``policies`` table). The Settings → Data Protection tab is the
 * ONLY consumer; multi-policy CRUD is intentionally out of scope.
 *
 * Mutations pass ``config()`` directly — DO NOT spread the Headers
 * object (v0.38.2 regression guard).
 */
const DataProtection = ({ url, config }: ServiceParams) => {
  const base = `${url}/v1/admin/policies`;

  return {
    /** List every policy on the caller's tenant. The tenant-default
     *  row is the one with ``user_id === null`` and ``is_active``. */
    async list(): Promise<LegacyPolicyOut[]> {
      const response = await fetch(base, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<LegacyPolicyOut[]>(response);
    },

    /** Create the tenant-default policy. ``user_id`` is omitted so the
     *  BE sets it NULL — that's how the resolver recognises the row. */
    async createTenantDefault(
      payload: CreateTenantDefaultPolicyPayload
    ): Promise<LegacyPolicyOut> {
      const response = await fetch(base, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(payload),
      });
      return unwrapJson<LegacyPolicyOut>(response);
    },

    /** Update the protection knobs on an existing policy. The legacy
     *  schema accepts every field as optional, so the partial payload
     *  below only touches the three we own. */
    async updateProtection(
      policyId: string,
      payload: UpdateDataProtectionPayload
    ): Promise<LegacyPolicyOut> {
      const response = await fetch(`${base}/${policyId}`, {
        method: "PATCH",
        headers: config(),
        body: JSON.stringify(payload),
      });
      return unwrapJson<LegacyPolicyOut>(response);
    },
  };
};

export default DataProtection;
